import React, { forwardRef, useEffect, useImperativeHandle, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "ag-grid-enterprise";
import {
  Input,
  Modal,
  Form,
  Tooltip,
  Popover,
  Card,
  Empty,
  Spin,
  Upload,
  message,
  Button,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LeftOutlined,
  PaperClipOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import $ from "jquery";
import "jquery-ui/ui/widgets/datepicker"; // side-effect: registers $.fn.datepicker
import ReportActionButtons from "components/ReportActionButtons";
import {
  getAdminPdtData,
  getPdtData,
  postAdminPdtData,
  postPdtData,
  approvePDTReport,
  submitPDTReport,
  partnerMarkMessageRead,
} from "../../../appRedux/actions/ProductDevelopmentTimeline";
import { showMessage, statusArray } from "../../../helpers";
import { diagnosticsStatusArray } from "../../../helpers/sample-response";
import {
  postDiagnosticsPDT,
  adminPostDiagnosticsPDT,
  uploadDiagnosticsDeliverable,
  getDiagnosticsDeliverables,
  deleteDiagnosticsDeliverable,
} from "../../../services/ProductDevTimeline";
import moment from "moment";
import { findIndex, isEmpty } from "lodash";
import { getRole } from "../../../helpers";
import { useNavigate, useParams } from "react-router-dom";
import "./pdt.css";
import LoadingBar from "components/LoadingBar";

const { confirm } = Modal;

function getDatePicker() {
  function Datepicker() {}
  Datepicker.prototype.init = function (params) {
    this.eInput = document.createElement("input");
    this.eInput.value = params.value;
    this.eInput.classList.add("ag-input");
    this.eInput.style.height = "100%";
    $(this.eInput).datepicker({ dateFormat: "mm/dd/yy" });
  };
  Datepicker.prototype.getGui = function () {
    return this.eInput;
  };
  Datepicker.prototype.afterGuiAttached = function () {
    this.eInput.focus();
    this.eInput.select();
  };
  Datepicker.prototype.getValue = function () {
    return this.eInput.value;
  };
  Datepicker.prototype.destroy = function () {};
  Datepicker.prototype.isPopup = function () {
    return false;
  };
  return Datepicker;
}

/** Max characters shown before "Show more" (collapsed view in grid cell). */
const PDT_SUMMARY_PREVIEW_MAX = 280;

/**
 * Modal-based editor for Summary cells.
 * AG Grid's inline popup sat beneath the sticky header, so we open a proper
 * Ant Design Modal instead. The editor tells AG Grid it's a popup so the grid
 * doesn't render anything inline, then immediately opens the modal.
 */
const SummaryCellEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(
    props.value != null && props.value !== "" ? String(props.value) : "",
  );
  const [visible, setVisible] = useState(true);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    isPopup: () => true,
  }));

  useEffect(() => {
    const v = props.value != null && props.value !== "" ? String(props.value) : "";
    setValue(v);
  }, [props.value]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => props.api.stopEditing(false), 0);
  };

  return (
    <Modal
      title="Edit Summary"
      open={visible}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      width={720}
      afterOpenChange={(open) => {
        if (open) {
          const inst = inputRef.current;
          if (inst?.focus) inst.focus();
          else if (inst?.resizableTextArea?.textArea) inst.resizableTextArea.textArea.focus();
        }
      }}
    >
      <Input.TextArea
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={10000}
        autoSize={{ minRows: 6, maxRows: 20 }}
        placeholder="Enter summary…"
      />
    </Modal>
  );
});

SummaryCellEditor.displayName = "SummaryCellEditor";

const SummaryCellRenderer = (params) => {
  const { value, data } = params;
  const text = value != null && value !== "" ? String(value) : "";
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [text]);


  if (!data || data.category !== "Diagnostics") {
    return <span style={{ color: "#999" }}>-</span>;
  }

  const isLong = text.length > PDT_SUMMARY_PREVIEW_MAX;
  const displayText =
    !expanded && isLong ? text.slice(0, PDT_SUMMARY_PREVIEW_MAX) : text;

  return (
    <div className="pdt-summary-cell-wrap">
      <div className="pdt-summary-cell-content">
        <div className="pdt-summary-cell-text">
          {displayText}
          {!expanded && isLong ? "…" : ""}
        </div>
      </div>
      {isLong ? (
        <Button
          type="link"
          size="small"
          className="pdt-summary-toggle"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      ) : null}
    </div>
  );
};

const DevelopmentTimeline = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const [isDataReady, setIsDataReady] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserStaff, setIsUserStaff] = useState(false);
  const [isUserDiagnosticAdmin, setIsUserDiagnosticAdmin] = useState(false);
  const [isUserDiagnosticReviewer, setIsUserDiagnosticReviewer] = useState(false);
  const [reportDetails, setReportDetails] = useState({});
  const [quarterEditDetails, setQuarterEditDetails] = useState({});
  const [messageCount, setMessageCount] = useState(0);
  const [partnerId, setPartnerId] = useState(null);
  const [gridApi, setGridApi] = useState({});
  const [rowData, setRowData] = useState(null);
  const [quarterDropdown, setQuarterDropdown] = useState([]);
  const [quarterDropdownCurrent, setQuarterDropdownCurrent] =
    useState("Loading...");
  const [isHistoricQuarter, setIsHistoricQuarter] = useState(false);
  const [isNotInitial, setIsNotInitial] = useState(false);
  const [hasExpandedInitially, setHasExpandedInitially] = useState(false);
  let [flag, setFlag] = useState(1);
  const [quarterOrder, setQuarterOrder] = useState([]);
  // Refs so DeliverablesCellRenderer (a stale AgGrid closure) can read
  // current permission/state values without being recreated.
  const isUserAdminRef = useRef(false);
  const isUserStaffRef = useRef(false);
  const isHistoricQuarterRef = useRef(false);
  const reportDetailsRef = useRef({});
  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "Products",
      field: "product",
      editable: false,
      rowGroup: true,
      hide: true,
      enableRowGroup: true,
    },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  // State for tracking deliverable files per row
  const [deliverableFiles, setDeliverableFiles] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  // Refs that mirror state so cell renderers (baked into column defs) always
  // read the latest value even when the closure is stale.
  const deliverableFilesRef = useRef({});
  const uploadingFilesRef = useRef({});
  // Refs for upload/remove handlers so the stale closure inside AgGrid column
  // defs always calls the latest version (avoids stale quarterDropdownCurrent).
  const handleDeliverableUploadRef = useRef(null);
  const handleDeliverableRemoveRef = useRef(null);
  // State for tracking if partner has any Diagnostics products
  const [hasDiagnosticsProducts, setHasDiagnosticsProducts] = useState(false);
  const { pdtList } = useSelector(({ pdt }) => pdt);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // Helper function to check if a product is Diagnostics
  const isDiagnosticsProduct = useCallback((data) => {
    return data && (
      data.category === 'Diagnostics' ||
      (data.product && data.product.toLowerCase().includes('diagnostics'))
    );
  }, []);

  // Handle file upload for deliverables - supports both single file and multiple files
  const handleDeliverableUpload = useCallback((fileOrFiles, rowData) => {
    if (!rowData || !rowData.stage_id) return;

    const isDiagnostics = rowData.category === 'Diagnostics';
    const key = isDiagnostics
      ? `diag_${rowData.active_product_id || diagnosticsActiveProductMapRef.current[rowData.product_id]}_${rowData.stage_id}`
      : `${rowData.product_id}_${rowData.stage_id}`;

    // Deduplicate: if beforeUpload fires multiple times for a multi-select,
    // only run once using a small ref guard keyed by the upload set.
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];

    updateUploadingFiles(prev => ({ ...prev, [key]: true }));

    const apiQuarterFormat = quarterDropdownCurrent.replace(/\s+to\s+/, '-');

    // Deliverables are Diagnostics-only; the renderer never calls this for FDF/API rows
    const uploadPromise = uploadDiagnosticsDeliverable(
      files,
      rowData.active_product_id || diagnosticsActiveProductMapRef.current[rowData.product_id],
      rowData.stage_id,
      apiQuarterFormat,
    );

    uploadPromise
      .then(response => {
        const uploadedFiles = [];

        if (response && response.files && Array.isArray(response.files)) {
          response.files.forEach((file, index) => {
            uploadedFiles.push({
              id: file.id,
              name: file.name,
              size: file.size,
              type: file.type,
              uid: file.id || Date.now() + index,
              url: file.url || file.file_url,
              serverId: file.id,
            });
          });
        } else if (response && response.id) {
          uploadedFiles.push({
            id: response.id,
            name: response.name || response.file_name,
            size: response.size || response.file_size,
            type: response.type || response.content_type,
            uid: response.id,
            url: response.url || response.file_url,
            serverId: response.id,
          });
        }

        // Merge into deliverableFiles — skip any IDs already present
        updateDeliverableFiles(prev => {
          const existingFiles = prev[key] || [];
          const existingIds = new Set(existingFiles.map(f => f.id).filter(Boolean));
          const newFiles = uploadedFiles.filter(f => !existingIds.has(f.id));
          if (newFiles.length === 0) return prev;
          return { ...prev, [key]: [...existingFiles, ...newFiles] };
        });

        if (uploadedFiles.length > 1) {
          message.success(`${uploadedFiles.length} files uploaded successfully`);
        } else if (uploadedFiles.length === 1) {
          message.success(`${uploadedFiles[0].name} uploaded successfully`);
        }
      })
      .catch(error => {
        console.error('Upload error:', error);
        message.error('Failed to upload files');
      })
      .finally(() => {
        updateUploadingFiles(prev => ({ ...prev, [key]: false }));
      });
  }, [quarterDropdownCurrent]);

  // Load existing deliverables from the server.
  // Deliverables are Diagnostics-only; FDF/API rows never show the column.
  const loadExistingDeliverables = useCallback(async (rows) => {
    if (!rows || rows.length === 0) return;

    const apiQuarterFormat = quarterDropdownCurrent.replace(/\s+to\s+/, '-');

    // Collect unique active_product_ids for Diagnostics products only
    const diagActiveProductIds = new Set();
    rows.forEach(row => {
      if (row.category !== 'Diagnostics' || !row.product_id) return;
      const apId = row.active_product_id || diagnosticsActiveProductMapRef.current[row.product_id];
      if (apId) diagActiveProductIds.add(apId);
    });

    try {
      for (const apId of diagActiveProductIds) {
        const response = await getDiagnosticsDeliverables(apId, null, apiQuarterFormat);
        if (response && response.deliverables) {
          response.deliverables.forEach(file => {
            const key = `diag_${apId}_${file.stage_id}`;
            const fileInfo = {
              id: file.id, name: file.file_name || file.name,
              size: file.file_size || file.size, type: file.file_type || file.type,
              uid: file.id, url: file.file_url || file.url, serverId: file.id,
            };
            updateDeliverableFiles(prev => {
              const existing = prev[key] || [];
              if (existing.some(f => f.id === file.id)) return prev;
              return { ...prev, [key]: [...existing, fileInfo] };
            });
          });
        }
      }
    } catch (error) {
      console.error('Error loading existing deliverables:', error);
    }
  }, [quarterDropdownCurrent]);

  // Handle file removal - also calls API to delete from server
  const handleDeliverableRemove = useCallback((file, rowData) => {
    if (!rowData || !rowData.stage_id) return;

    const isDiagnostics = rowData.category === 'Diagnostics';
    const key = isDiagnostics
      ? `diag_${rowData.active_product_id || diagnosticsActiveProductMapRef.current[rowData.product_id]}_${rowData.stage_id}`
      : `${rowData.product_id}_${rowData.stage_id}`;

    if (file.serverId || file.id) {
      const fileId = file.serverId || file.id;
      const deleteCall = deleteDiagnosticsDeliverable(fileId);
      deleteCall
        .then(() => message.success(`${file.name} removed`))
        .catch(error => {
          console.error('Error deleting file:', error);
          message.error(`Failed to remove ${file.name}`);
        });
    }

    // Only update the deliverableFiles map — DeliverablesCellRenderer reads deliverableFilesRef
    // as its source of truth and the refreshCells effect will redraw the cell.
    // Do NOT call setRowData here: passing a new rowData array to AgGrid collapses all row groups
    // (groupDefaultExpanded={0} + no getRowId means AG Grid treats each update as a fresh dataset).
    updateDeliverableFiles(prev => {
      const existingFiles = prev[key] || [];
      return {
        ...prev,
        [key]: existingFiles.filter(f => f.uid !== file.uid && f.serverId !== file.serverId),
      };
    });
  }, []);

  // Maps product_id -> active_product_id for Diagnostics products (updated in setValues)
  const diagnosticsActiveProductMapRef = useRef({});

  // Helpers that keep state and ref in sync atomically
  const updateDeliverableFiles = useCallback((updater) => {
    setDeliverableFiles(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      deliverableFilesRef.current = next;
      return next;
    });
  }, []);

  const updateUploadingFiles = useCallback((updater) => {
    setUploadingFiles(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      uploadingFilesRef.current = next;
      return next;
    });
  }, []);

  // Keep handler refs pointing at the latest function after every render.
  // This lets the stale closure inside AgGrid column defs always call the
  // current implementation (with up-to-date quarterDropdownCurrent, etc.).
  handleDeliverableUploadRef.current = handleDeliverableUpload;
  handleDeliverableRemoveRef.current = handleDeliverableRemove;
  // Keep permission refs current so DeliverablesCellRenderer reads live values.
  isUserAdminRef.current = isUserAdmin;
  isUserStaffRef.current = isUserStaff;
  isHistoricQuarterRef.current = isHistoricQuarter;
  reportDetailsRef.current = reportDetails;

  // Fetch deliverables from the API once per selected quarter when row data is ready.
  // Do not tie this to every rowData change — local updates (e.g. deleting an attachment)
  // were re-triggering a full reload, racing DELETE, and resetting grid/popover state.
  const deliverablesLoadedQuarterRef = useRef(null);

  const DeliverablesCellRenderer = (props) => {
    const { data } = props;

    // Deliverables column is Diagnostics-only
    if (!data || data.category !== 'Diagnostics') {
      return <span style={{ color: '#999' }}>-</span>;
    }

    const isDiagRow = data.category === 'Diagnostics';
    const diagApId = data.active_product_id
      || diagnosticsActiveProductMapRef.current[data.product_id];
    const key = isDiagRow && diagApId
      ? `diag_${diagApId}_${data.stage_id}`
      : `${data.product_id}_${data.stage_id}`;
    // Read from refs (always current) rather than the stale closure values
    const files = deliverableFilesRef.current[key] || data.deliverables || [];
    const isUploading = uploadingFilesRef.current[key] || false;

    // Note: data.editable only marks a stage's first row for the product_status
    // cascade — it does not restrict attachments, which are independent per stage.
    const isEditable = (
      isUserAdminRef.current || (!isUserStaffRef.current && !isHistoricQuarterRef.current && reportDetailsRef.current.report_status !== "Approved")
    );

    const uploadProps = {
      name: 'file',
      multiple: true,
      showUploadList: false,
      accept: '*/*',
      beforeUpload: (file, fileList) => {
        // Ant Design calls beforeUpload once per file in a multi-select.
        // Only trigger the upload on the first call to avoid sending all
        // files multiple times.
        // Use the ref so we always call the latest handleDeliverableUpload
        // (which has the current quarterDropdownCurrent in its closure).
        if (isEditable && file === fileList[0]) {
          handleDeliverableUploadRef.current(fileList, data);
        }
        return false;
      },
      disabled: !isEditable || isUploading,
      key: `${key}-${files.length}`,
    };

    // Popover content shown when files exist: file list + "+" upload button at the bottom
    const popoverContent = (
      <div style={{ minWidth: 220, maxWidth: 320 }}>
        {files.map((file, index) => (
          <div
            key={file.uid || index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '5px 0',
              borderBottom: '1px solid #f0f0f0',
              gap: '8px',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', fontSize: '12px', flex: 1 }}>
              <PaperClipOutlined style={{ flexShrink: 0, color: '#1d3c6e' }} />
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                title={file.name}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1d3c6e', textDecoration: 'none' }}
                onMouseEnter={e => (e.target.style.textDecoration = 'underline')}
                onMouseLeave={e => (e.target.style.textDecoration = 'none')}
              >
                {file.name}
              </a>
            </span>
            {isEditable && (
              <DeleteOutlined
                onClick={() => handleDeliverableRemoveRef.current(file, data)}
                style={{ cursor: 'pointer', color: '#ff4d4f', flexShrink: 0 }}
              />
            )}
          </div>
        ))}

        {isEditable && (
          <div style={{ paddingTop: '8px' }}>
            <Upload {...uploadProps}>
              <Tooltip title={isUploading ? 'Uploading…' : 'Attach more files'}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  fontSize: '12px', color: '#1d3c6e',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1,
                }}>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', lineHeight: 1 }}>+</span>
                  Attach more
                </span>
              </Tooltip>
            </Upload>
          </div>
        )}
      </div>
    );

    return (
      <div className="deliverables-cell"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', height: '100%' }}>

        {files.length > 0 ? (
          // Has files: clip icon + count pill → opens popover
          <Popover
            content={popoverContent}
            title={`${files.length} file${files.length > 1 ? 's' : ''} attached`}
            trigger="click"
            placement="bottomLeft"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
              <PaperClipOutlined style={{ fontSize: '16px', color: '#1d3c6e' }} />
              <span style={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#fff',
                backgroundColor: '#1d3c6e',
                borderRadius: '10px',
                padding: '0 5px',
                lineHeight: '16px',
                minWidth: '16px',
                textAlign: 'center',
              }}>
                {files.length}
              </span>
            </span>
          </Popover>
        ) : isEditable ? (
          // No files + editable: single clip → triggers upload directly
          <Upload {...uploadProps}>
            <Tooltip title={isUploading ? 'Uploading…' : 'Attach file'}>
              <PaperClipOutlined
                style={{
                  fontSize: '16px',
                  color: isUploading ? '#aaa' : '#1d3c6e',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                }}
              />
            </Tooltip>
          </Upload>
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    setPDT();
  }, []);

  // Admin: navigating to another partner must allow deliverables to load again for the same quarter.
  useEffect(() => {
    deliverablesLoadedQuarterRef.current = null;
  }, [params.id]);

  // Load existing deliverables from the server once per quarter (not on every rowData edit).
  useEffect(() => {
    if (!rowData || rowData.length === 0 || !quarterDropdownCurrent) {
      return;
    }
    if (quarterDropdownCurrent === "Loading...") {
      return;
    }
    if (deliverablesLoadedQuarterRef.current === quarterDropdownCurrent) {
      return;
    }
    deliverablesLoadedQuarterRef.current = quarterDropdownCurrent;
    loadExistingDeliverables(rowData);
  }, [rowData, quarterDropdownCurrent, loadExistingDeliverables]);

  useEffect(() => {
    if (isDataReady) {
      addQuarter();
    }
  }, [isDataReady]);

  useEffect(() => {
    if (isNotInitial) {
      addQuarter();
    }
  }, [flag]);

  // Refresh the Deliverables column in AgGrid whenever file state changes so
  // cell renderers pick up new uploads/deletes without requiring a page reload.
  useEffect(() => {
    if (gridApi && gridApi.refreshCells) {
      gridApi.refreshCells({ force: true, columns: ['deliverables', 'summary'] });
    }
  }, [deliverableFiles, gridApi]);

  useEffect(() => {
    // Keep all product groups collapsed by default on initial load
    // (groupDefaultExpanded={0} already handles this; just mark as done).
    if (gridApi && rowData && rowData.length > 0 && !hasExpandedInitially && isDataReady) {
      setHasExpandedInitially(true);
    }
  }, [rowData, gridApi, hasExpandedInitially, isDataReady]);

  const navigateBack = () => {
    if (isUserAdmin || isUserStaff) {
      navigate("/admin/partner-list");
    }
  };

  const setPDT = (quarter_name = null) => {
    setIsLoading(true);
    const id = params.id;
    if (id) {
      setIsUserAdmin(() => {
        return getRole() === "ADMIN";
      });
      setIsUserStaff(() => {
        return getRole() === "STAFF";
      });
      setIsUserDiagnosticAdmin(() => getRole() === "DIAGNOSTIC_ADMIN");
      setIsUserDiagnosticReviewer(() => getRole() === "DIAGNOSTIC_REVIEWER");
      dispatch(
        getAdminPdtData(
          id,
          (data) => {
            setValues(data);
            setPartnerId(id);
            setIsLoading(false);
          },
          quarter_name,
        ),
      );
    } else {
      setIsUserAdmin(() => {
        return getRole() === "ADMIN";
      });
      setIsUserStaff(() => {
        return getRole() === "STAFF";
      });
      setIsUserDiagnosticAdmin(() => getRole() === "DIAGNOSTIC_ADMIN");
      setIsUserDiagnosticReviewer(() => getRole() === "DIAGNOSTIC_REVIEWER");
      dispatch(
        getPdtData((data) => {
          setValues(data);
          setIsLoading(false);
        }, quarter_name),
      );
    }
  };

  const setValues = (data) => {
    // Create a new array to avoid mutating Redux state
    
    // DEBUG: Log quarter-related data from API
    console.log('=== DEBUG: API Quarter Data ===');
    console.log('data.quarter_order:', JSON.stringify(data.quarter_order));
    console.log('data.quarter_dropdown:', JSON.stringify(data.quarter_dropdown));
    console.log('data.pdt_meta.quarter_name:', data.pdt_meta ? data.pdt_meta.quarter_name : 'N/A');
    console.log('================================');

    // Step 1: identify Diagnostics products and collect active_product_id mapping
    const diagnosticsProducts = new Set();
    // Map from product_id -> active_product_id (for Diagnostics deliverable calls)
    const diagnosticsActiveProductMap = {};

    data.rows.forEach(row => {
      if (row.category === 'Diagnostics') {
        diagnosticsProducts.add(row.product);
        if (row.active_product_id && row.product_id) {
          diagnosticsActiveProductMap[row.product_id] = row.active_product_id;
        }
      }
    });

    // Set whether partner has any Diagnostics products
    const hasDiagnostics = diagnosticsProducts.size > 0;
    setHasDiagnosticsProducts(hasDiagnostics);

    // Step 2: Propagate stage 1's status to subsequent stages of the same product
    // (works uniformly for FDF/API and Diagnostics — the backend now sends
    // product_status only on stage 1 of every product).
    let cascadeStatus = null;
    const updatedRows = data.rows.map(row => {
      const newRow = { ...row };
      if (newRow.product_status) {
        cascadeStatus = newRow.product_status;
      } else {
        newRow.product_status = cascadeStatus;
      }
      return newRow;
    });

    // Persist the Diagnostics active_product_id map for use in save/deliverables
    diagnosticsActiveProductMapRef.current = diagnosticsActiveProductMap;

    setRowData(updatedRows);
    setQuarterOrder(data.quarter_order);
    setMessageCount(data.unread_message_count);
    setReportDetails(data.pdt_meta);
    setQuarterEditDetails(data.quarter_editable);
    setShowReport(!isEmpty(data));

    const transformedQuarterDropdown = data.quarter_dropdown.map(quarter =>
      quarter.replace(/-/g, ' to ')
    );
    setQuarterDropdown(transformedQuarterDropdown);

    setQuarterDropdownCurrent(data.quarter_order[0]);
    setFlag(++flag);
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    setIsDataReady(true);
  };

  const addQuarter = () => {
    const { report_status } = reportDetails;

    // Same permission rule the status cells always used.
    const canEditStatus = isUserAdmin
      ? true
      : isUserStaff
        ? false
        : isHistoricQuarter
          ? false
          : report_status !== "Approved";

    const column = [
      {
        headerName: "Products",
        field: "product",
        editable: false,
        rowGroup: true,
        hide: true,
        enableRowGroup: true,
      },
      {
        // One Status column, three kinds of cell:
        //  * product (group) row        -> the product status, editable
        //  * Diagnostics stage row      -> that stage's own status, editable
        //  * FDF/API stage row          -> the product status, read-only (changed on the product row)
        headerName: "Status",
        field: "product_status",
        width: 150,
        valueGetter: (params) => {
          if (params.node.group) {
            const leaf = params.node.allLeafChildren && params.node.allLeafChildren[0];
            return leaf && leaf.data ? leaf.data.product_status : undefined;
          }
          if (!params.data) return undefined;
          return isDiagnosticsProduct(params.data) ? params.data.stage_status : params.data.product_status;
        },
        valueFormatter: (params) => {
          const leaf = params.node && params.node.allLeafChildren && params.node.allLeafChildren[0];
          const rowData = params.data || (leaf && leaf.data);
          // Product row (any category): FDF/API statuses. Diagnostics stage row: stage statuses.
          const list = isDiagnosticsProduct(rowData) && !params.node.group ? diagnosticsStatusArray : statusArray;
          const found = list.find((s) => s.id === params.value);
          return found ? found.name : params.value;
        },
        editable: (params) =>
          canEditStatus && (!!params.node.group || (!!params.data && isDiagnosticsProduct(params.data))),
        valueSetter: (params) => {
          if (!params.node.group) {
            // Diagnostics stage row: this stage's own status.
            params.data.stage_status = params.newValue;
            return true;
          }
          // Product row: the product status applies to every stage row of the product.
          (params.node.allLeafChildren || []).forEach((leaf) => {
            if (leaf.data) leaf.data.product_status = params.newValue;
          });
          params.node.data = undefined; // AG Grid puts {} on a group row while setting a value
          params.api.refreshCells({ force: true });
          return false;
        },
        cellClass: (params) => {
          const { node, data } = params;
          if (node && node.group) return canEditStatus ? "editable-cell" : "non-editable-cell";
          return canEditStatus && data && isDiagnosticsProduct(data) ? "editable-cell" : "non-editable-cell";
        },
        cellEditorSelector: (params) => {
          const list = params.node.group ? statusArray : diagnosticsStatusArray;
          return {
            component: "agRichSelect",
            params: {
              values: list.map((value) => value.id),
              searchDebounceDelay: 0,
              allowTyping: true,
              filterList: true,
              highlightMatch: true,
            },
          };
        },
      },
      {
        headerName: "Notes",
        field: "notes",
        editable: (params) => {
          // Phase header rows are not editable
          if (params.data && params.data.is_phase_header === true) {
            return false;
          }
          return isUserAdmin
            ? true
            : isUserStaff
              ? false
              : isHistoricQuarter
                ? false
                : report_status !== "Approved";
        },
        cellClass: (params) => {
          const { data } = params;
          if (data) {
            // Phase header rows have special styling
            if (data.is_phase_header === true) {
              return 'phase-header-row';
            }
            return (
              isUserAdmin
                ? true
                : isUserStaff
                  ? false
                  : isHistoricQuarter
                    ? false
                    : report_status !== "Approved"
            )
              ? "editable-cell"
              : "non-editable-cell";
          }
        },
        width: 250,
      },
    ];

    const obj = {
      headerName: reportDetails.quarter_name,
      children: [
        {
          headerName: "Start Date (MM/DD/YYYY)",
          field: "start_date_" + reportDetails.quarter_name,
          editable: isUserAdmin
            ? true
            : isUserStaff
              ? false
              : isHistoricQuarter
                ? false
                : quarterEditDetails[reportDetails.quarter_name] === false
                  ? false
                  : report_status !== "Approved",
          cellEditor: "datePicker",
          cellClass: (params) => {
            const { data } = params;
            if (data) {
              return (
                isUserAdmin
                  ? true
                  : isUserStaff
                    ? false
                    : isHistoricQuarter
                      ? false
                      : quarterEditDetails[reportDetails.quarter_name] === false
                        ? false
                        : report_status !== "Approved"
              )
                ? "editable-cell"
                : "non-editable-cell";
            }
          },
          cellClassRules: {
            "error-cell": (params) => {
              const { value } = params;
              return !checkDate(value) && value;
            },
          },
          sortable: false,
          width: 200,
        },
        {
          headerName: "End Date (MM/DD/YYYY)",
          field: "end_date_" + reportDetails.quarter_name,
          sortable: false,
          editable: isUserAdmin
            ? true
            : isUserStaff
              ? false
              : isHistoricQuarter
                ? false
                : quarterEditDetails[reportDetails.quarter_name] === false
                  ? false
                  : report_status !== "Approved",
          cellClass: (params) => {
            const { data } = params;
            if (data) {
              return (
                isUserAdmin
                  ? true
                  : isUserStaff
                    ? false
                    : isHistoricQuarter
                      ? false
                      : quarterEditDetails[reportDetails.quarter_name] === false
                        ? false
                        : report_status !== "Approved"
              )
                ? "editable-cell"
                : "non-editable-cell";
            }
          },
          cellClassRules: {
            "error-cell": (params) => {
              const { value } = params;
              return !checkDate(value) && value;
            },
          },
          cellEditor: "datePicker",
          width: 200,
        },
      ],
    };
    column.push(obj);

    // Add Deliverables column group only when the partner has Diagnostics products
    if (hasDiagnosticsProducts) {
      const isEditableCondition = isUserAdmin
        ? true
        : isUserStaff
          ? false
          : isHistoricQuarter
            ? false
            : report_status !== "Approved";

      column.push({
        headerName: "Deliverables",
        children: [
          {
            headerName: "Summary",
            field: "summary",
            width: 320,
            minWidth: 200,
            // Enter/Shift+Enter must insert newlines in the textarea, not stop editing (grid default).
            suppressKeyboardEvent: (params) =>
              params.editing && params.event.key === "Enter",
            editable: (params) => {
              if (params.data && params.data.is_phase_header === true) return false;
              if (params.data && params.data.category !== 'Diagnostics') return false;
              return isEditableCondition;
            },
            cellEditor: "summaryCellEditor",
            cellClass: (params) => {
              const { data } = params;
              if (!data) return;
              if (data.is_phase_header === true) return 'phase-header-row';
              if (data.category !== 'Diagnostics') return 'non-editable-cell';
              const base =
                data.editable && isEditableCondition ? "editable-cell" : "non-editable-cell";
              return `${base} pdt-summary-cell`;
            },
            cellRenderer: SummaryCellRenderer,
          },
          {
            headerName: "Attachment",
            field: "deliverables",
            width: 130,
            cellRenderer: DeliverablesCellRenderer,
            cellClass: (params) => {
              const { data } = params;
              if (data) {
                if (data.is_phase_header === true) {
                  return 'phase-header-row';
                }
                return isEditableCondition
                  ? "editable-cell"
                  : "non-editable-cell";
              }
            },
          },
        ],
      });
    }

    // enableGroupEdit (needed so the product row's Status can be edited) makes AG Grid ask each
    // column's `editable` for group rows too -- keep every other column non-editable there.
    const blockGroupRowEdit = (col) => {
      if (col.children) {
        col.children.forEach(blockGroupRowEdit);
        return;
      }
      if (col.field === "product_status" || col.editable === undefined) return;
      const original = col.editable;
      col.editable = (params) =>
        !params.node.group && (typeof original === "function" ? original(params) : !!original);
    };
    column.forEach(blockGroupRowEdit);

    setColumnDefs(column);
    gridApi.setColumnDefs(column);
    handleUndefinedValues();
    setIsNotInitial(true);
    
    // After setting column definitions, we need to re-expand the first group
    // Reset the expansion flag so it can expand again after the grid refresh
    if (hasExpandedInitially) {
      setHasExpandedInitially(false);
    }
  };

  const handleUndefinedValues = () => {
    let tempRowData = [...rowData];
    gridApi.forEachNode((node) => {
      const { data } = node;
      if (data) {
        quarterOrder.forEach((value) => {
          if (
            !Object.prototype.hasOwnProperty.call(data, "start_date_" + value)
          ) {
            const rowIdx = findIndex(tempRowData, { stage_id: data.stage_id });
            if (rowIdx !== -1) {
              tempRowData[rowIdx]["start_date_" + value] = null;
              tempRowData[rowIdx]["end_date_" + value]   = null;
            }
          }
        });
      }
    });
    
    // Store current expansion state before updating data
    let expandedGroups = [];
    try {
      gridApi.forEachNode((node) => {
        if (node.group && node.expanded) {
          expandedGroups.push(node.key);
        }
      });
    } catch (error) {
      console.warn('Error getting expanded groups:', error);
    }

    setRowData(tempRowData);
    
    // Restore expansion state after data update
    if (expandedGroups.length > 0) {
      setTimeout(() => {
        try {
          gridApi.forEachNode((node) => {
            if (node.group && expandedGroups.includes(node.key)) {
              node.setExpanded(true);
            }
          });
        } catch (error) {
          console.warn('Error restoring expanded groups:', error);
        }
      }, 100);
    }
  };

  const checkDataValues = (dataList) => {
    return !dataList.some((value) => checkRow(value));
  };

  const checkRow = (rowData) => {
    let statusFlag = false;

    for (let key in rowData) {
      if (Object.prototype.hasOwnProperty.call(rowData, key)) {
        if (
          key === "product_status" &&
          rowData["editable"] &&
          rowData["product_status"] &&
          !statusArray
            .map((value) => value.id)
            .includes(rowData["product_status"])
        ) {
          statusFlag = true;
          break;
        }
      }
    }
    let dateFlag = Object.keys(rowData)
      .filter(
        (value) =>
          value.split("_")[0] === "start" || value.split("_")[0] === "end",
      )
      .some(
        (value) =>
          !checkDate(rowData[value]) &&
          rowData[value] !== undefined &&
          rowData[value] !== null &&
          rowData[value] !== "",
      );

    return statusFlag || dateFlag;
  };

  const saveReportBeforeAction = (callback) => {
    setIsActionLoading(true);
    gridApi.stopEditing();
    gridApi.selectAll();

    const fdfApiObj = {};
    const diagnosticsMap = {};

    let selectedNodes = gridApi.getSelectedNodes()
      .filter((d) => d.data)
      .map(node => ({ ...node.data }));

    if (!checkDataValues(selectedNodes)) {
      setIsActionLoading(false);
      Modal.error({ title: "Invalid data!", content: "Please input valid entries!" });
      gridApi.deselectAll();
      return;
    }

    const quarterDisplayName = reportDetails.quarter_name;
    const start_date_key = "start_date_" + quarterDisplayName;
    const end_date_key   = "end_date_"   + quarterDisplayName;

    selectedNodes.forEach((node) => {
      const {
        product_id, active_product_id, stage_id,
        editable, product_status, stage_status, notes, summary, category,
      } = node;

      const isDiagnostics = category === 'Diagnostics';

      if (isDiagnostics) {
        // ── Diagnostics: build per-stage payload for the new endpoint ──────────
        // active_product_id may come from the row or from the ref map
        const apId = active_product_id
          || diagnosticsActiveProductMapRef.current[product_id];
        if (!apId || !stage_id) return;

        if (!diagnosticsMap[apId]) diagnosticsMap[apId] = { productStatus: null, stages: [] };
        // product status is product-level: take it from the product's first stage row
        if (product_status && (editable || !diagnosticsMap[apId].productStatus)) {
          diagnosticsMap[apId].productStatus = product_status;
        }

        const start_date = node[start_date_key];
        const end_date   = node[end_date_key];

        diagnosticsMap[apId].stages.push({
          stage_id,
          // each stage saves its own status (independent of the product status)
          status: stage_status || 'NOT_STARTED',
          start_date: start_date === "" ? null : start_date || null,
          end_date:   end_date   === "" ? null : end_date   || null,
          notes: notes || null,
          summary: summary || null,
        });
      } else {
        // ── FDF / API: unchanged payload format ────────────────────────────────
        if (!fdfApiObj[product_id]) fdfApiObj[product_id] = {};
        if (!fdfApiObj[product_id][stage_id]) fdfApiObj[product_id][stage_id] = {};

        if (editable) {
          fdfApiObj[product_id]["product_status"] = product_status;
        }

        const currentQuarter = quarterDisplayName.replace(/\s+to\s+/, '-');
        const start_date = node[start_date_key];
        const end_date   = node[end_date_key];

        fdfApiObj[product_id][stage_id]["notes"] = notes;
        fdfApiObj[product_id][stage_id][currentQuarter] = {
          start_date: start_date === "" ? null : start_date,
          end_date:   end_date   === "" ? null : end_date,
        };
      }
    });

    gridApi.deselectAll();

    // Build all save promises
    const savePromises = [];

    // Save on behalf of a partner: ADMIN/STAFF and DIAGNOSTIC_ADMIN must hit
    // the admin endpoints — partner endpoints scope by request.user.id.
    const role = getRole();
    const useAdminEndpoints =
      isUserAdmin || role === "STAFF" || role === "DIAGNOSTIC_ADMIN";

    // 1. FDF / API save (old endpoint) — only if there is FDF/API data
    if (Object.keys(fdfApiObj).length > 0) {
      const fdfApiPromise = new Promise((resolve, reject) => {
        if (useAdminEndpoints) {
          // quarterDropdownCurrent is in display format ("Jan to Mar 2025"); the API
          // needs the stored quarter_name format ("Jan-Mar 2025"), same conversion used
          // everywhere else this value is sent to the backend.
          const args = isHistoricQuarter
            ? [partnerId, fdfApiObj, resolve, quarterDropdownCurrent.replace(/\s+to\s+/, '-')]
            : [partnerId, fdfApiObj, resolve];
          dispatch(postAdminPdtData(...args));
        } else {
          dispatch(postPdtData(fdfApiObj, resolve));
        }
      });
      savePromises.push(fdfApiPromise);
    }

    // 2. Diagnostics save (new endpoint) — one call per active_product_id
    Object.entries(diagnosticsMap).forEach(([apId, { productStatus, stages }]) => {
      const diagPromise = useAdminEndpoints
        ? adminPostDiagnosticsPDT(Number(apId), quarterDisplayName, stages, productStatus)
        : postDiagnosticsPDT(Number(apId), quarterDisplayName, stages, productStatus);
      savePromises.push(diagPromise);
    });

    Promise.all(savePromises)
      .then(() => {
        setIsActionLoading(false);
        callback();
      })
      .catch(() => {
        setIsActionLoading(false);
        Modal.error({ title: "Save failed", content: "An error occurred while saving. Please try again." });
      });
  };

  const saveData = () => {
    saveReportBeforeAction(() => {
      // Maintain the current quarter when refetching after save
      const currentQuarter = isHistoricQuarter ? quarterDropdownCurrent.replace(/\s+to\s+/, '-') : null;
      setPDT(currentQuarter);
      showMessage("success", "Data recorded successfully!");
    });
  };

  const showSubmitConfirm = () => {
    confirm({
      content: (
        <Spin spinning={isActionLoading}>
          {showCommentsForm()}
        </Spin>
      ),
      okText: "Submit",
      cancelText: "Cancel",
      title: "Do you want to submit the report?",
      className: "btn-confirm-green",
      onOk(close) {
        form.validateFields().then((values) => {
          let msg = values.commentData ? values.commentData : "";
          // Save the grid data first, then submit the report
          saveReportBeforeAction(() => {
            dispatch(
              submitPDTReport({ message: msg }, () => {
                setIsActionLoading(false);
                showMessage(
                  "success",
                  "Report has been submitted to the admin!",
                );
                const currentQuarter = isHistoricQuarter ? quarterDropdownCurrent.replace(/\s+to\s+/, '-') : null;
                setPDT(currentQuarter);
                form.resetFields(["commentData"]);
              }),
            );
          });
          close();
        });
      },
      onCancel() {
        form.resetFields(["commentData"]);
      },
    });
  };

  const approver = (msg, isApproved) => {
    setIsActionLoading(true);
    let data = {
      partner_id: partnerId,
      message: msg,
      is_approved: isApproved,
    };
    dispatch(
      approvePDTReport(data, () => {
        setIsActionLoading(false);
        if (isApproved) {
          showMessage("success", "Report Approved!");
        } else {
          showMessage("success", "Report Rejected!");
        }
        form.resetFields(["commentData"]);
        // Maintain the current quarter when refetching after approve/reject
        const currentQuarter = isHistoricQuarter ? quarterDropdownCurrent.replace(/\s+to\s+/, '-') : null;
        setPDT(currentQuarter);
      }),
    );
  };

  const showCommentsForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>
            {isUserAdmin
              ? "Add comments for partner:"
              : "Add comments for admin:"}
          </span>
        </div>
        <Form.Item
          name="commentData"
          className="mb-0"
          rules={[
            {
              required: isUserAdmin,
              message: isUserAdmin
                ? "Enter comments for partner"
                : "Enter comments for admin",
            },
          ]}
        >
          <Input.TextArea placeholder="Enter comments here" />
        </Form.Item>
      </Form>
    );
  };

  const showRejectConfirm = () => {
    confirm({
      title: "Do you want to reject the report?",
      className: "btn-confirm-red",
      content: (
        <Spin spinning={isActionLoading}>
          {showCommentsForm()}
        </Spin>
      ),
      okText: "Reject",
      cancelText: "Cancel",
      icon: <CloseCircleOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          let msg = values.commentData ? values.commentData : "";
          approver(msg, false);
          close();
        });
      },
      onCancel() {
        form.resetFields(["commentData"]);
      },
    });
  };

  const showApproveConfirm = () => {
    confirm({
      title: "Do you want to approve the report?",
      className: "btn-confirm-green",
      content: (
        <Spin spinning={isActionLoading}>
          {showCommentsForm()}
        </Spin>
      ),
      icon: <CheckCircleOutlined />,
      okText: "Approve",
      cancelText: "Cancel",
      onOk(close) {
        form.validateFields().then((values) => {
          let msg = values.commentData ? values.commentData : "";
          approver(msg, true);
          close();
        });
      },
      onCancel() {
        form.resetFields(["commentData"]);
      },
    });
  };

  const displayHeader = () => {
    if (showReport && reportDetails) {
        const {
          partner_name,
          quarter_name,
          report_status,
          approval_time,
          submission_time,
        } = reportDetails;
        return (
          <div className="gx-mb-4">
            <h1 className="title ">
              {isUserAdmin || isUserStaff ? (
                <Tooltip title="Back">
                  <LeftOutlined className="mr-10" onClick={navigateBack} />
                </Tooltip>
              ) : null}
              Product Development Timeline{" "}
              {isUserAdmin || isUserStaff ? (
                <span className="mr-5">
                  for <span className="text-capitalize">{partner_name}</span>
                </span>
              ) : null}
              <span className="text-capitalize">({quarter_name})</span>
            </h1>
            <h4 className={isUserAdmin || isUserStaff ? "ml-30" : ""}>
              Report Status:
              <span className="text-capitalize"> {" " + report_status}</span>
              <span>
                {!approval_time && !submission_time
                  ? ""
                  : " on " +
                    moment(
                      report_status === "Submitted" ||
                        report_status === "Resubmitted"
                        ? submission_time
                        : approval_time,
                    ).format("Do MMM YYYY, hh:mm A")}
              </span>
            </h4>
          </div>
        );
    } else {
      return (
        <div className="gx-mb-4">
          <h1 className="title ">
            {isUserAdmin ? (
              <LeftOutlined className="mr-5" onClick={navigateBack} />
            ) : null}
            Product Development Timeline{" "}
            {isUserAdmin && reportDetails ? (
              <span>
                for{" "}
                <span className="text-capitalize">
                  {reportDetails.partner_name}
                </span>
              </span>
            ) : null}
          </h1>
        </div>
      );
    }
  };

  const displayCTA = () => {
    if (showReport && reportDetails) {
      const { report_status } = reportDetails;

      if (isUserAdmin || isUserStaff || isUserDiagnosticAdmin || isUserDiagnosticReviewer) {
        // Admin/Staff/Diagnostic-Admin/Reviewer view: Save, Approve, Reject, Download, Mail, Quarter Dropdown
        return (
          <ReportActionButtons
            // Quarter dropdown props
            quarterDropdownCurrent={quarterDropdownCurrent}
            onQuarterChange={changeQuarter}
            quarterDropdown={quarterDropdown}

            // Action button props
            onSave={handleSave}
            onApprove={handleApprove}
            onReject={handleReject}
            onDownload={handleDownload}

            // Permission props
            isUserAdmin={isUserAdmin}
            isUserStaff={isUserStaff}
            isHistoricQuarter={isHistoricQuarter}

            // Report status
            reportStatus={report_status}

            // Notification props
            messageCount={messageCount}
            messages={pdtList && pdtList.messages ? pdtList.messages : []}
            onNotificationClick={handleNotificationClick}

            // Disable states
            disableSave={isUserStaff || isUserDiagnosticReviewer}
            disableApprove={isHistoricQuarter || isUserStaff || isUserDiagnosticReviewer}
            disableReject={isHistoricQuarter || isUserStaff || isUserDiagnosticReviewer}

            // Show/hide specific buttons - Admin/Staff configuration
            showSave={true}
            showApprove={true}
            showReject={true}
            showSubmit={false}  // No submit for admin/staff
            showDownload={true}
            showQuarterDropdown={quarterDropdown && quarterDropdown.length > 0}

            // Skip confirmation props
            skipApproveConfirm={true}
            skipRejectConfirm={true}

            // Custom styles
            className="development-timeline-actions"
          />
        );
      } else {
        // Partner view: Save, Submit Report, Mail, Download, Quarter Dropdown
        return (
          <ReportActionButtons
            // Quarter dropdown props
            quarterDropdownCurrent={quarterDropdownCurrent}
            onQuarterChange={changeQuarter}
            quarterDropdown={quarterDropdown}

            // Action button props
            onSave={handleSave}
            onSubmit={handleSubmit}
            onDownload={handleDownload}

            // Permission props
            isUserAdmin={isUserAdmin}
            isUserStaff={isUserStaff}
            isHistoricQuarter={isHistoricQuarter}

            // Report status
            reportStatus={report_status}

            // Notification props
            messageCount={messageCount}
            messages={pdtList && pdtList.messages ? pdtList.messages : []}
            onNotificationClick={handleNotificationClick}

            // Disable states
            disableSave={report_status === "Approved" || isHistoricQuarter}
            disableSubmit={report_status === "Approved" || isHistoricQuarter}

            // Show/hide specific buttons - Partner configuration
            showSave={true}
            showApprove={false}
            showReject={false}
            showSubmit={true}
            showDownload={true}
            showQuarterDropdown={quarterDropdown && quarterDropdown.length > 0}
            skipSubmitConfirm={true}

            // Custom styles
            className="development-timeline-actions"
          />
        );
      }
    } else {
      return null;
    }
  };

  const downloadExcel = () => {
    gridApi.selectAll();
    gridApi.exportDataAsExcel({
      sheetName: "pdt",
      fileName: "pdt",
    });
    gridApi.deselectAll();
  };

  const checkDate = (value) => {
    return (
      moment(value, "M/D/YYYY").format("M/D/YYYY") === value ||
      moment(value, "MM/DD/YYYY").format("MM/DD/YYYY") === value
    );
  };

  const handleCellValueChange = (params) => {
    const { colDef, node, newValue, oldValue, api } = params;
    let fieldNameList = colDef.field.split("_");

    if (newValue !== oldValue) {
      if (colDef.field === "summary") {
        try {
          (api || gridApi).resetRowHeights();
          (api || gridApi).refreshCells({
            force: true,
            rowNodes: [node],
            columns: ["summary"],
          });
        } catch (error) {
          console.warn("Error resizing summary row:", error);
        }
        return;
      }

      if (colDef.field === "product_status") {
        // A Diagnostics stage row's Status is that stage's own status: nothing to cascade.
        // (Product-row edits are applied to all stage rows by the column's valueSetter.)
        if (node.data && isDiagnosticsProduct(node.data)) return;
        // The status is only editable on a product's first stage row, but it
        // represents the whole product. Cascade the new value to the other
        // stage rows of the same product so the change is visible across the
        // whole group right away (this only updates local display state —
        // it does not change what gets sent to the backend).
        const productId = node.data.product_id;

        let expandedGroups = [];
        try {
          (api || gridApi).forEachNode((gridNode) => {
            if (gridNode.group && gridNode.expanded) {
              expandedGroups.push(gridNode.key);
            }
          });
        } catch (error) {
          console.warn('Error getting expanded groups in handleCellValueChange:', error);
        }

        const updatedRowData = rowData.map((row) =>
          row.product_id === productId ? { ...row, product_status: newValue } : row
        );
        setRowData(updatedRowData);

        if (expandedGroups.length > 0) {
          setTimeout(() => {
            try {
              (api || gridApi).forEachNode((gridNode) => {
                if (gridNode.group && expandedGroups.includes(gridNode.key)) {
                  gridNode.setExpanded(true);
                }
              });
            } catch (error) {
              console.warn('Error restoring expanded groups in handleCellValueChange:', error);
            }
          }, 50);
        }
        return;
      }
      
      // Handle date field changes (start/end)
      if (fieldNameList[0] === "start" || fieldNameList[0] === "end") {
        if (newValue === " ") {
          // Store current expansion state before updating data
          let expandedGroups = [];
          try {
            gridApi.forEachNode((gridNode) => {
              if (gridNode.group && gridNode.expanded) {
                expandedGroups.push(gridNode.key);
              }
            });
          } catch (error) {
            console.warn('Error getting expanded groups in handleCellValueChange:', error);
          }
          
          // Update row data immutably
          const updatedRowData = rowData.map(row => {
            if (row.stage_id === node.data.stage_id) {
              return {
                ...row,
                [colDef.field]: null
              };
            }
            return row;
          });

          setRowData(updatedRowData);
          
          // Restore expansion state after data update
          if (expandedGroups.length > 0) {
            setTimeout(() => {
              try {
                gridApi.forEachNode((gridNode) => {
                  if (gridNode.group && expandedGroups.includes(gridNode.key)) {
                    gridNode.setExpanded(true);
                  }
                });
              } catch (error) {
                console.warn('Error restoring expanded groups in handleCellValueChange:', error);
              }
            }, 50);
          }
        }
      }
    }
  };

  const handleRowGroupOpened = (params) => {
    // This ensures that row group expansion/collapse works properly
    console.log('Row group opened:', params.node.key, params.node.expanded);
    
    // If this is a manual user interaction, don't let our auto-expansion interfere
    if (params.node.expanded === false && hasExpandedInitially) {
      // User manually collapsed a group, respect their choice
      console.log('User manually collapsed group, respecting choice');
    }
  };

  const setReportHeight = () => {
    return window.innerHeight - 120;
  };

  const changeQuarter = (e) => {
    // Handle both event object (from select) and direct value (from ReportActionButtons)
    const quarterValue = e.target ? e.target.value : e;
    
    // Convert "Jan to Mar 2025" format to "Jan-Mar 2025" format for API
    const apiQuarterFormat = quarterValue.replace(/\s+to\s+/, '-');

    setColumnDefs([
      {
        headerName: "Products",
        field: "product",
        editable: false,
        rowGroup: true,
        hide: true,
        enableRowGroup: true,
      },
    ]);
    
    // Reset expansion flag when changing quarters
    setHasExpandedInitially(false);

    if (quarterValue !== quarterDropdown[0]) {
      setPDT(apiQuarterFormat);
      setIsHistoricQuarter(true);
    } else {
      setPDT();
      setIsHistoricQuarter(false);
    }
  };

  // Action handlers for ReportActionButtons
  const handleSave = () => {
    saveData();
  };

  const handleApprove = () => {
    showApproveConfirm();
  };

  const handleReject = () => {
    showRejectConfirm();
  };

  const handleSubmit = () => {
    showSubmitConfirm();
  };

  const handleDownload = () => {
    downloadExcel();
  };

  const handleNotificationClick = () => {
    dispatch(
      partnerMarkMessageRead(
        isUserAdmin ? "admin" : "partner",
        () => setMessageCount(0),
      ),
    );
  };

  return (
    <div>
      {isLoading ? (
        <div>
          <LoadingBar />
        </div>
      ) : (
        <>
          <Spin spinning={isActionLoading} tip="Processing...">
            <div className="gx-flex-row gx-justify-content-between">
              {displayHeader()}
              {displayCTA()}
            </div>
            {showReport ? (
              <div
                className="ag-theme-balham ag-scroll-bar-fix"
                style={{ height: setReportHeight(), paddingBottom: 20 }}
              >
                <AgGridReact
                  popupParent={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  onGridReady={onGridReady}
                  enableGroupEdit={true}
                  columnDefs={columnDefs}
                  rowData={rowData}
                  defaultColGroupDef={{ marryChildren: true }}
                  defaultColDef={{
                    filter: true,
                    sortable: true,
                    width: 300,
                    resizable: true,
                  }}
                  autoGroupColumnDef={{
                    headerName: "Products",
                    field: "stage",
                    editable: false,
                    minWidth: 250,
                    cellRendererParams: {
                      suppressCount: true,
                    },
                  }}
                  getRowHeight={(params) => {
                    if (params.node.group) return 32;
                    if (
                      params.data &&
                      params.data.category === 'Diagnostics' &&
                      params.data.summary
                    ) {
                      const text = String(params.data.summary);
                      const lineCount =
                        text.split(/\r?\n/).length +
                        Math.floor(text.length / 60);
                      const estimated = 24 + lineCount * 20;
                      return Math.min(200, Math.max(72, estimated));
                    }
                    return 32;
                  }}
                  suppressRowTransform={true}
                  enableRangeSelection={true}
                  enableCellChangeFlash={true}
                  onCellValueChanged={handleCellValueChange}
                  onRowGroupOpened={handleRowGroupOpened}
                  onFirstDataRendered={(params) => {
                    params.api.resetRowHeights();
                  }}
                  onRowDataUpdated={(params) => {
                    params.api.resetRowHeights();
                  }}
                  animateRows={true}
                  groupDefaultExpanded={0}
                  suppressAggFuncInHeader={true}
                  components={{
                    datePicker: getDatePicker(),
                    summaryCellEditor: SummaryCellEditor,
                  }}
                />
              </div>
            ) : (
              <Card>
                <Empty
                  description={
                    <p className="empty-msg">
                      <span>No Data Available</span>
                      <br />
                      <span>Please contact Admin for more information!</span>
                    </p>
                  }
                />
              </Card>
            )}
          </Spin>
        </>
      )}
    </div>
  );
};

export default DevelopmentTimeline;